import React, { useCallback, useEffect, useRef, useState } from "react"
import { Badge, Button, Card, CardBody, Spinner } from "reactstrap"
import { disconnectAdminWhatsapp, getAdminWhatsappStatus, setupAdminWhatsapp } from "helpers/backendHelpers/adminWhatsapp"

const STATUS_POLL_INTERVAL_MS = 3000
const QR_REFRESH_INTERVAL_MS = 45000

const toQrImageSource = qr => (qr ? (qr.startsWith("data:") ? qr : `data:image/png;base64,${qr}`) : null)
const formatPhoneNumber = ownerJid => {
  const number = ownerJid && String(ownerJid).split("@")[0].split(":")[0]
  return number ? `+${number}` : null
}

const AdminWhatsappCard = () => {
  const [status, setStatus] = useState("not_connected")
  const [qrBase64, setQrBase64] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const refreshInFlightRef = useRef(false)

  const applyPayload = useCallback(payload => {
    const connectionStatus = payload?.status?.connectionStatus || "not_connected"
    const latestQr = payload?.qrBase64 || payload?.status?.latestQrBase64 || null
    setStatus(connectionStatus)
    setQrBase64(connectionStatus === "connected" ? null : latestQr)
    setProfile(connectionStatus === "connected" ? (payload?.profile || {
      name: payload?.status?.profileName || null,
      picture: payload?.status?.profilePicture || null,
      ownerJid: payload?.status?.ownerJid || null,
    }) : null)
    return connectionStatus
  }, [])

  const loadStatus = useCallback(async () => {
    try {
      const response = await getAdminWhatsappStatus()
      return applyPayload(response?.payload)
    } catch (_) {
      return null
    }
  }, [applyPayload])

  const refreshQr = useCallback(async ({ showLoading = false } = {}) => {
    if (refreshInFlightRef.current) return
    refreshInFlightRef.current = true
    if (showLoading) setActionLoading(true)
    try {
      const response = await setupAdminWhatsapp()
      applyPayload(response?.payload)
    } catch (_) {
      // The shared API helper displays the error; retain the last QR on screen.
    } finally {
      if (showLoading) setActionLoading(false)
      refreshInFlightRef.current = false
    }
  }, [applyPayload])

  useEffect(() => {
    let active = true
    const initialize = async () => {
      const currentStatus = await loadStatus()
      if (active && currentStatus === "connecting") {
        // A cached QR may already have expired while the page was closed.
        await refreshQr()
      }
      if (active) setLoading(false)
    }
    initialize()
    return () => {
      active = false
    }
  }, [loadStatus, refreshQr])

  useEffect(() => {
    if (status !== "connecting") return undefined

    const statusInterval = window.setInterval(loadStatus, STATUS_POLL_INTERVAL_MS)
    const qrRefreshInterval = window.setInterval(refreshQr, QR_REFRESH_INTERVAL_MS)

    return () => {
      window.clearInterval(statusInterval)
      window.clearInterval(qrRefreshInterval)
    }
  }, [loadStatus, refreshQr, status])

  const handleConnect = () => refreshQr({ showLoading: true })

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect Admin WhatsApp?")) return
    setActionLoading(true)
    try {
      await disconnectAdminWhatsapp()
      setStatus("not_connected")
      setQrBase64(null)
      setProfile(null)
    } catch (_) {
      // The shared API helper displays the error.
    } finally {
      setActionLoading(false)
    }
  }

  const qrSource = toQrImageSource(qrBase64)
  const isConnected = status === "connected"
  const isConnecting = status === "connecting"
  const phoneNumber = formatPhoneNumber(profile?.ownerJid)

  return (
    <Card>
      <CardBody className="text-center py-4">
                <div className="d-flex justify-content-between align-items-center mb-4 text-start">
                  <div>
                    <h5 className="mb-1"><i className="bx bxl-whatsapp text-success me-2" />Admin WhatsApp</h5>
                    <p className="text-muted mb-0">Connect one WhatsApp account for the Admin panel.</p>
                  </div>
                  <Badge color={isConnected ? "success" : isConnecting ? "warning" : "secondary"} pill className="px-3 py-2 text-capitalize">
                    {status.replace("_", " ")}
                  </Badge>
                </div>

                {loading ? (
                  <div className="py-5"><Spinner color="success" /><p className="text-muted mt-3 mb-0">Checking WhatsApp connection...</p></div>
                ) : isConnected ? (
                  <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 py-3 text-md-start">
                    <div className="d-flex align-items-center text-start">
                      {profile?.picture ? (
                        <img
                          src={profile.picture}
                          alt={profile.name || "Admin WhatsApp profile"}
                          className="rounded-circle me-3"
                          style={{ width: 64, height: 64, objectFit: "cover" }}
                        />
                      ) : (
                        <div className="rounded-circle bg-success-subtle text-success d-inline-flex align-items-center justify-content-center me-3" style={{ width: 64, height: 64, fontSize: 32 }}>
                          <i className="bx bxl-whatsapp" />
                        </div>
                      )}
                      <div>
                        <p className="mb-1 fw-medium">{profile?.name || "WhatsApp Account"}</p>
                        {phoneNumber && <p className="text-muted mb-0">{phoneNumber}</p>}
                      </div>
                    </div>
                    <p className="text-muted mb-0">This Admin WhatsApp account is ready to use.</p>
                    <Button color="danger" disabled={actionLoading} onClick={handleDisconnect}>
                      {actionLoading ? <Spinner size="sm" /> : "Disconnect WhatsApp"}
                    </Button>
                  </div>
                ) : isConnecting && qrSource ? (
                  <div>
                    <p className="mb-3">Scan this QR code with WhatsApp on your phone.</p>
                    <img src={qrSource} alt="Admin WhatsApp QR code" style={{ width: 300, maxWidth: "100%" }} />
                    <p className="text-warning mt-3 mb-3"><i className="bx bx-loader-circle bx-spin me-1" />Waiting for scan...</p>
                    <p className="text-muted small mb-3">The QR code refreshes automatically if it expires.</p>
                    <Button color="success" disabled={actionLoading} onClick={handleConnect}>
                      {actionLoading ? <Spinner size="sm" /> : "Refresh QR"}
                    </Button>
                  </div>
                ) : (
                  <div className="py-5">
                    <i className="bx bxl-whatsapp text-success" style={{ fontSize: 64 }} />
                    <h5 className="mt-3">Connect Admin WhatsApp</h5>
                    <p className="text-muted">Generate a QR code and scan it from WhatsApp on your phone.</p>
                    <Button color="success" disabled={actionLoading} onClick={handleConnect}>
                      {actionLoading ? <><Spinner size="sm" className="me-1" />Generating QR...</> : "Connect WhatsApp"}
                    </Button>
                  </div>
                )}
      </CardBody>
    </Card>
  )
}

export default AdminWhatsappCard
